import { Router, RouterModule } from '@angular/router';
import { from, of } from 'rxjs';
import { StatusBar, Style } from '@capacitor/status-bar';
import { switchMap } from 'rxjs/operators';
import { Component, computed, effect, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { ContentStoreSelector } from '@common/selectors/content.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { Paths } from '@config/paths.config';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import { NativeService } from '@core/services/native/native.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { ShopSearchLocationInputComponent } from '@shared/components/shop-search-location-input/shop-search-location-input.component';
import { RoundedIconButtonComponent } from '../rounded-icon-button/rounded-icon-button.component';
import { ShopSearchParametersOverviewComponent } from '@shared/components/shop-search-parameters-overview/shop-search-parameters-overview.component';
import { LinkDirective } from '@core/directives/link.directive';
import { BlockListComponent } from '@core/components/block-list/block-list.component';
import { ButtonAtomComponent } from '../button-atom/button-atom.component';
import { HeaderLanguageFlagAtomComponent } from '../header-language-flag-atom/header-language-flag-atom.component';
import { HamburgerMenuAtomComponent } from '../hamburger-menu-atom/hamburger-menu-atom.component';
import { CartParametersOverviewComponent } from '@shared/components/cart-parameters-overview/cart-parameters-overview.component';
import { CartUtils } from '@app/utilities/cart.util';

export interface LogoOptions {
  position?: 'left' | 'center';
  maxWidth?: string;
  maxHeight?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

@Component({
  selector: 'app-header-atom',
  templateUrl: './header-atom.component.html',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ContentBuilderColorPipe,
    ShopSearchLocationInputComponent,
    RoundedIconButtonComponent,
    ShopSearchParametersOverviewComponent,
    LinkDirective,
    BlockListComponent,
    ButtonAtomComponent,
    HeaderLanguageFlagAtomComponent,
    HamburgerMenuAtomComponent,
    CartParametersOverviewComponent,
  ],
})
export class HeaderAtomComponent extends DestroyerBase {
  readonly hamburgerClick = output<Event>();
  readonly languageFlagClick = output<Event>();
  readonly logoImageUrl = input<string>();
  readonly logoOptions = input<LogoOptions>();
  readonly backgroundColor = input<IContentBuilderFieldColor>({
    value: 'transparent',
    type: ColorFieldTypesValues.Hex,
  });
  readonly hamburgerColor = input<IContentBuilderFieldColor>({
    value: 'neutral-800',
    type: ColorFieldTypesValues.Palette,
  });
  readonly labelColor = input<IContentBuilderFieldColor>({
    value: 'neutral-800',
    type: ColorFieldTypesValues.Palette,
  });
  readonly iconColor = input<IContentBuilderFieldColor>({
    value: 'primary-600',
    type: ColorFieldTypesValues.Palette,
  });
  readonly iconBackgroundColor = input<IContentBuilderFieldColor>({
    value: 'primary-100',
    type: ColorFieldTypesValues.Palette,
  });
  readonly buttonTextColor = input<IContentBuilderFieldColor>({
    value: '#FFFFFF',
    type: ColorFieldTypesValues.Hex,
  });
  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>({
    value: 'primary-600',
    type: ColorFieldTypesValues.Palette,
  });

  readonly displayHamburger = input(true);
  readonly displayLogo = input(false);
  readonly displayBackButton = input(true);
  readonly backLinkUrl = input.required<string>();
  readonly leftBlocks = input<any>();
  readonly rightBlocks = input<any>();
  readonly navigationLinks = input<any[]>();
  readonly title = input<string>();
  readonly positionAbsoluteOnMobile = input(false);
  readonly positionAbsoluteOnDesktop = input(false);
  readonly languageFlagUrl = input<string>();
  readonly shopSearchLocationFieldBackgroundColor = input<IContentBuilderFieldColor | undefined>(
    undefined,
  );
  readonly shopSearchLocationFieldTextColor = input<IContentBuilderFieldColor | undefined>(
    undefined,
  );
  readonly shopSearchLocationFieldPlaceholderColor = input<IContentBuilderFieldColor | undefined>(
    undefined,
  );
  readonly shopSearchLocationFieldIconColor = input<IContentBuilderFieldColor | undefined>(
    undefined,
  );

  private readonly router = inject(Router);
  private readonly cartRefiner = inject(CartStoreRefiner);
  private readonly authSelector = inject(AuthStoreSelector);
  private readonly contentSelector = inject(ContentStoreSelector);
  private readonly settingsSelector = inject(SettingsStoreSelector);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly navigationHistoryService = inject(NavigationHistoryService);
  protected readonly titleSignal = toSignal(this.contentSelector.selectHeaderTitle, {
    initialValue: undefined,
  });
  protected readonly isShopSearchAddressVisibleSignal = toSignal(
    this.contentSelector.selectHeaderIsShopSearchAddressVisible,
    { initialValue: false },
  );
  protected readonly isShopSearchParametersVisibleSignal = toSignal(
    this.contentSelector.selectHeaderIsShopSearchParametersVisible,
    { initialValue: false },
  );
  protected readonly isCartParametersVisibleSignal = toSignal(
    this.contentSelector.selectHeaderIsCartParametersVisible,
    { initialValue: false },
  );
  protected readonly isCartButtonVisibleSignal = toSignal(
    this.contentSelector.selectHeaderIsCartButtonVisible,
    { initialValue: false },
  );

  protected readonly cartProductsSignal = toSignal(this.cartRefiner.selectActiveCartProducts, {
    initialValue: [],
  });

  /** Total units in cart (sum of line quantities), not distinct line count */
  protected readonly cartLineItemsTotalQuantity = computed(() =>
    CartUtils.sumActiveCartLineQuantities(this.cartProductsSignal()),
  );

  private readonly statusStepSignal = toSignal(this.authSelector.selectStatusStep, {
    initialValue: undefined,
  });

  protected isNavigationToCartInProgress = false;
  protected isOpenStaticNav = false;
  protected isNotched = false;
  protected isMobileScreenType = false;

  constructor() {
    super();

    // Device screen type
    effect(() => {
      const isMobileScreenType = this.settingsSelector.deviceIsMobileScreen();
      this.isMobileScreenType = isMobileScreenType;
    });

    // Navigation status
    effect(() => {
      const step = this.statusStepSignal();
      if (!step) {
        this.isNavigationToCartInProgress = false;
      }
    });

    // Notch
    from(NativeService.isNotched())
      .pipe(
        switchMap(_isNotched => {
          this.isNotched = _isNotched;
          return _isNotched ? from(StatusBar.setStyle({ style: Style.Dark })) : of();
        }),
      )
      .subscribe();
  }

  protected onClick(event: Event): void {
    this.hamburgerClick.emit(event);
  }

  protected onLanguageFlagClick(event: Event): void {
    this.languageFlagClick.emit(event);
  }

  protected toggleStaticNav(): void {
    this.isOpenStaticNav = !this.isOpenStaticNav;
  }

  protected back(): void {
    const backLinkUrl = this.backLinkUrl();
    if (!backLinkUrl) {
      this.navigationHistoryService.goBack();
    } else if (!this.routerHelper.isLinkExternal(backLinkUrl)) {
      this.router.navigate([
        backLinkUrl ? this.routerHelper.translateRoute(backLinkUrl) : backLinkUrl,
      ]);
    } else {
      window.location.href = backLinkUrl;
    }
  }

  protected navigateToCart(): void {
    this.isNavigationToCartInProgress = true;
    this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
  }
}
